import { useCallback } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import classNames from 'classnames';
import { ChevronLeft, ChevronRight, Close } from '@carbon/icons-react';
import {
  tcNavigableUnitsAtom,
  tcSelectedDevAreaAtom,
  tcSelectDevAreaRequestAtom,
} from './type-curve-filters/atoms';
import styles from './tc-unit-navigator.module.scss';

// Prev/next navigator that steps through the filtered development-area units.
// Shared by the toolbar above the map and the gun-barrel panel so both stay in
// lockstep on the same selection. Prev/next route through
// `tcSelectDevAreaRequestAtom` — the map layer resolves the requested name
// (zoom, highlight, compute the section-line endpoints) and writes the full
// selection back to `tcSelectedDevAreaAtom`, which the gun barrel reads. The
// clear button resets the selection directly. Always rendered — shows
// "No unit selected (N available)" until a unit is picked.
interface TcUnitNavigatorProps {
  /**
   * Drop the "UNIT" label, the unit name and the clear button, leaving just
   * prev/next and the position. The Unit Builder's title bar already names the
   * unit, so repeating it there reads as a duplicate — the mock's builder header
   * shows only `(37/111)` between its chevrons.
   */
  compact?: boolean;
  /** Drop only the "UNIT" label, keeping the unit name, chevrons and clear. */
  hideLabel?: boolean;
  /** Drop the bottom rule and outer padding so the navigator sits inline. */
  borderless?: boolean;
}

export const TcUnitNavigator = ({
  compact = false,
  hideLabel = false,
  borderless = false,
}: TcUnitNavigatorProps) => {
  const units = useAtomValue(tcNavigableUnitsAtom);
  const selected = useAtomValue(tcSelectedDevAreaAtom);
  const setSelected = useSetAtom(tcSelectedDevAreaAtom);
  const requestSelect = useSetAtom(tcSelectDevAreaRequestAtom);

  const count = units.length;
  const currentIdx = selected
    ? units.findIndex((u) => u.name === selected.developmentAreaName)
    : -1;

  const goToPrev = useCallback(() => {
    if (currentIdx <= 0 || count === 0) return;
    requestSelect(units[currentIdx - 1].name);
  }, [currentIdx, count, units, requestSelect]);

  const goToNext = useCallback(() => {
    if (count === 0) return;
    if (currentIdx < 0) {
      requestSelect(units[0].name);
      return;
    }
    if (currentIdx >= count - 1) return;
    requestSelect(units[currentIdx + 1].name);
  }, [currentIdx, count, units, requestSelect]);

  const clearSelection = useCallback(() => {
    setSelected(null);
  }, [setSelected]);

  return (
    <div
      className={classNames(styles.unitNavigator, {
        [styles.borderless]: borderless,
      })}
    >
      {!compact && !hideLabel && <span className={styles.unitLabel}>UNIT</span>}
      <button
        type="button"
        className={styles.navButton}
        onClick={goToPrev}
        disabled={!selected || currentIdx <= 0}
        aria-label="Previous unit"
      >
        <ChevronLeft size={16} />
      </button>
      {selected ? (
        <span className={styles.unitName}>
          {!compact && selected.developmentAreaName}
          {currentIdx >= 0 && (
            <span className={styles.unitIndex}>
              {!compact && ' '}({currentIdx + 1}/{count})
            </span>
          )}
        </span>
      ) : (
        <span className={styles.unitNameEmpty}>
          {compact ? `(0/${count})` : `No unit selected (${count} available)`}
        </span>
      )}
      <button
        type="button"
        className={styles.navButton}
        onClick={goToNext}
        disabled={count === 0 || currentIdx >= count - 1}
        aria-label="Next unit"
      >
        <ChevronRight size={16} />
      </button>
      {!compact && (
        <button
          type="button"
          className={styles.navButton}
          onClick={clearSelection}
          disabled={!selected}
          aria-label="Clear unit selection"
        >
          <Close size={16} />
        </button>
      )}
    </div>
  );
};

TcUnitNavigator.displayName = 'TcUnitNavigator';
